import { Box, Divider, List, Typography } from '@mui/material';
import React, { useState } from 'react';

import DocumentsViewerModal from '../DocumentsViewer';

import AttachmentRow from './AttachmentRow';
import AttachmentsDropzone from './AttachmentsDropzone';
import AttachmentsEmptyState from './AttachmentsEmptyState';
import AttachmentsListHeader from './AttachmentsListHeader';
import type { AttachmentsListProps } from './types';

import { ACCEPTED_ATTACHMENT_FORMATS } from '@/utils/constants';

export type { AttachmentListItem, AttachmentsListProps } from './types';

const AttachmentsList: React.FC<AttachmentsListProps> = ({
  attachments,
  loading = false,
  emptyMessage = 'No files yet. Upload with the button above or drag and drop here.',
  accept = ACCEPTED_ATTACHMENT_FORMATS,
  maxFiles = 10,
  isUploading = false,
  onUploadFiles,
  onAssociationTypeChange,
}) => {
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = React.useRef(0);

  const handleButtonSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files;
    if (chosen?.length) onUploadFiles(Array.from(chosen));
    e.target.value = '';
  };

  const hasFiles = (e: React.DragEvent) =>
    e.dataTransfer.types.includes('Files') || e.dataTransfer.types.includes('application/x-moz-file');

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasFiles(e) || isUploading) return;
    dragCounterRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (!hasFiles(e) || isUploading) return;
    const files = e.dataTransfer.files;
    if (files?.length) onUploadFiles(Array.from(files).slice(0, maxFiles));
  };

  const handleView = (previewUrl: string) => setViewerUrl(previewUrl);

  const handleOpenInNewTab = (previewUrl: string) => {
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = (previewUrl: string) => {
    const a = document.createElement('a');
    a.href = previewUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.setAttribute('download', '');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" overflow="hidden">
      <AttachmentsListHeader accept={accept} isUploading={isUploading} onFileSelect={handleButtonSelect} />
      <Divider />
      <AttachmentsDropzone
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : attachments.length === 0 && !isUploading ? (
          <AttachmentsEmptyState message={emptyMessage} />
        ) : (
          <List dense disablePadding>
            {attachments.map((att) => (
              <AttachmentRow
                key={att.id}
                attachment={att}
                selected={selectedId === att.id}
                onSelect={() => setSelectedId(att.id)}
                onView={handleView}
                onOpenInNewTab={handleOpenInNewTab}
                onDownload={handleDownload}
                onAssociationTypeChange={onAssociationTypeChange}
              />
            ))}
          </List>
        )}
      </AttachmentsDropzone>

      {viewerUrl && <DocumentsViewerModal urls={[viewerUrl]} handleClose={() => setViewerUrl(null)} />}
    </Box>
  );
};

export default AttachmentsList;
